import Service, { service } from '@ember/service';
import { isBlank } from '@ember/utils';
import type AuthzService from 'garaje/services/authz';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureConfigService from 'garaje/services/feature-config';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import includesAny from 'garaje/utils/includes-any';
import { GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST } from 'garaje/utils/roles';
import { Permission } from 'garaje/utils/ui-permissions';
import { intersect, notEmpty, reads } from 'macro-decorators';

const WORKPLACE_ADMIN_ROLES = Object.freeze([GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST]);

type WorkplaceRouteConfig =
  | { type: 'off' }
  | { type: 'start-trial' }
  | {
      type: 'legacy-protect';
      canVisitSettings: boolean;
      canVisitAnnouncements: boolean;
      canVisitEmployeeLog: boolean;
      hasEmployeeScreeningFlow: boolean;
    }
  | {
      type: 'subscribed';
      canVisitSettings: boolean;
      canVisitAnnouncements: boolean;
      canVisitEmployeeLog: boolean;
      hasEmployeeScreeningFlow: boolean;
    };

export default class WorkplaceRouteConfigService extends Service {
  @service declare authz: AuthzService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureConfig: FeatureConfigService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare state: StateService;

  adminRoles = WORKPLACE_ADMIN_ROLES;

  @reads('state.workplaceSubscription') subscription!: StateService['workplaceSubscription'];
  @reads('state.vrSubscription') vrSubscription!: StateService['vrSubscription'];
  @reads('currentAdmin.roleNames') roles!: CurrentAdminService['roleNames'];
  @intersect('roles', 'adminRoles') userAdminRoles!: string[];
  @notEmpty('userAdminRoles') hasAdminRole!: boolean;

  get hasEmployeeScreeningFlow(): boolean {
    if (this.state.currentLocation?.hasEmployeeScreeningFlow) return true;
    if (this.state.employeeScreeningFlowService?.activeEmployeeScreeningFlow) return true;

    return false;
  }

  get hasVisitEmployeeLogPermission(): boolean {
    return this.authz.hasAnyPermissionAtCurrentLocation([
      Permission.WORKPLACE_EMPLOYEE_INVITE_READ,
      Permission.DESKS_RESERVATION_READ,
    ]);
  }

  get canVisitEmployeeLog(): boolean {
    if (this.hasVisitEmployeeLogPermission) return true;
    if (this.canUseDbeam && this.featureConfig.isEnabled('adminConfig.canViewAllReservations')) return true;

    return false;
  }

  /**
   * @returns whether user has permissions to view/inspect employee registration flow settings
   */
  get canViewEmployeeRegistrationFlow(): boolean {
    if (!this.featureFlags.isEnabled('workplace-permission-employee-registration-flow')) {
      return includesAny(this.roles, GLOBAL_ADMIN, LOCATION_ADMIN);
    }

    return (
      this.authz.hasPermissionAtCurrentCompany(Permission.WORKPLACE_EMPLOYEE_FLOW_INSPECT) ||
      this.authz.hasPermissionAtCurrentLocation(Permission.WORKPLACE_EMPLOYEE_FLOW_INSPECT)
    );
  }

  /**
   * @returns whether user has permissions to edit employee registration flow structure
   */
  get canEditEmployeeRegistrationFlow(): boolean {
    if (!this.featureFlags.isEnabled('workplace-permission-employee-registration-flow')) {
      return includesAny(this.roles, GLOBAL_ADMIN);
    }

    return this.authz.hasAnyPermissionAtCurrentCompany([
      Permission.WORKPLACE_EMPLOYEE_FLOW_CREATE,
      Permission.WORKPLACE_EMPLOYEE_FLOW_UPDATE,
      Permission.WORKPLACE_EMPLOYEE_FLOW_DELETE,
    ]);
  }

  get canAccessEmployeeFlowSettings(): boolean {
    return this.canViewEmployeeRegistrationFlow || this.canEditEmployeeRegistrationFlow;
  }

  get canManageEmployeeScheduling(): boolean {
    if (!this.featureFlags.isEnabled('workplace-permission-employee-scheduling-settings')) return false;

    return this.hasLocationUpdatePermission || this.hasRegistrationConfigPermission;
  }

  get hasLocationUpdatePermission(): boolean {
    return (
      this.authz.hasPermissionAtCurrentLocation(Permission.ACCOUNT_MANAGEMENT_LOCATION_UPDATE) ||
      this.authz.hasPermissionAtCurrentCompany(Permission.ACCOUNT_MANAGEMENT_LOCATION_UPDATE)
    );
  }

  get hasRegistrationConfigPermission(): boolean {
    return (
      this.authz.hasPermissionAtCurrentLocation(Permission.WORKPLACE_EMPLOYEE_REGISTRATION_CONFIGURATION_UPDATE) ||
      this.authz.hasPermissionAtCurrentCompany(Permission.WORKPLACE_EMPLOYEE_REGISTRATION_CONFIGURATION_UPDATE)
    );
  }

  get canUseDbeam(): boolean {
    if (!!this.state.features?.canUseDbeam && this.featureFlags.isEnabled('dbeam-ea-rollout')) return true;
    if (!!this.state.features?.canAccessProtectLegacyFeatures && this.featureFlags.isEnabled('dbeam-allowlist'))
      return true;

    return false;
  }

  get canVisitAnnouncements(): boolean {
    if (!this.featureFlags.isEnabled('permission-manage-employee-announcement')) return this.hasAdminRole;

    return this.authz.hasAnyPermissionAtCurrentLocation([
      Permission.WORKPLACE_EMPLOYEE_ANNOUNCEMENT_CREATE,
      Permission.WORKPLACE_EMPLOYEE_ANNOUNCEMENT_READ,
      Permission.WORKPLACE_EMPLOYEE_ANNOUNCEMENT_UPDATE,
      Permission.WORKPLACE_EMPLOYEE_ANNOUNCEMENT_DELETE,
    ]);
  }

  computeWorkplaceRouteConfig(): WorkplaceRouteConfig {
    if (!this.#canAccessWorkplaceFeatures) return { type: 'off' };
    if (this.#isStartTrial) return { type: 'start-trial' };

    return {
      type: this.#isLegacyProtect ? 'legacy-protect' : 'subscribed',
      canVisitAnnouncements: this.canVisitAnnouncements,
      canVisitEmployeeLog: this.hasAdminRole || this.canVisitEmployeeLog,
      canVisitSettings: this.hasAdminRole || this.canAccessEmployeeFlowSettings || this.canManageEmployeeScheduling,
      hasEmployeeScreeningFlow: this.hasEmployeeScreeningFlow,
    };
  }

  get #canAccessProtectLegacyFeatures(): boolean {
    return !!this.vrSubscription?.canAccessProtectLegacyFeatures;
  }

  get #canAccessWorkplaceApplication(): boolean {
    if (!this.featureFlags.isEnabled('wrkx-pricing-packaging')) return false;
    if (!this.state.features?.canAccessWorkplaceApplication) return false;

    return true;
  }

  get #isStartTrial(): boolean {
    return isBlank(this.subscription) && !this.#canAccessProtectLegacyFeatures && !this.#canAccessWorkplaceApplication;
  }

  get #isLegacyProtect(): boolean {
    return isBlank(this.subscription) && this.#canAccessProtectLegacyFeatures && !this.#canAccessWorkplaceApplication;
  }

  get #canAccessWorkplaceFeatures(): boolean {
    if (!this.featureFlags.isEnabled('workplace-garaje-launch')) return false;
    if (this.hasAdminRole) return true;
    if (this.canVisitEmployeeLog) return true;
    if (this.canAccessEmployeeFlowSettings) return true;
    if (this.canManageEmployeeScheduling) return true;
    if (this.canVisitAnnouncements) return true;

    return false;
  }
}
